/* eslint-disable @typescript-eslint/no-explicit-any */
import { useParams, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { 
  useEquitySecurityDetail, 
  useUpdateEquitySecurity, 
  useDeleteEquitySecurity,
  type EquitySecurity 
} from '@equitySecurityHooks';
import { useState } from 'react';
import { toast } from 'sonner';
import { 
  PageHeaderSkeleton, 
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, 
  AlertCircle, 
  Edit, 
  Trash2, 
  TrendingUp, 
  TrendingDown,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import EquityForm from '@/pages/investments/securities/equity/EquityForm';
import StockPriceDisplay from '@/pages/investments/securities/equity/StockPriceDisplay';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { SecuritiesErrorState } from '@/components/securities-error-state/SecuritiesErrorState';
import { useFetchEquityDividends } from '@equitySecurityHooks';
import { computeTotalEquityDividends, calculateEquityProfitLoss } from '@equityUtils';
import { DividendHistoryTable } from '@/components/dividend-history-table/DividendHistoryTable';
import { SharePurchaseTrendChart } from '@/components/share-purchase-trend-chart/SharePurchaseTrendChart';
import { useFetchSecuritiesTransactions } from '@/hooks/securities/useSecuritiesTransactions.ts';
import { useRefreshStockPrice } from '@/hooks/useRefreshStockPrice';

const PAGE_SIZE = 10;

/**
 * EquityDetailPage component displays detailed information for a single equity security.
 * Accessible at /investments/securities/equity/:id
 */
export default function EquityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Dialog state
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dividendsPage, setDividendsPage] = useState(1);

  // Fetch equity security detail
  const { 
    data: equityDetail, 
    isLoading, 
    error,
    refetch,
  } = useEquitySecurityDetail(id!);

  // Fetch dividends for this equity
  const {
    data: dividendsData,
    isLoading: isDividendsLoading,
    isError: isDividendsError,
    refetch: refetchDividends,
  } = useFetchEquityDividends(id!, dividendsPage, PAGE_SIZE);

  // Fetch raw transactions for share purchase trend chart
  const {
    data: transactions,
    isLoading: isTransactionsLoading,
  } = useFetchSecuritiesTransactions('equity', id!);

  // Mutation hooks
  const updateEquity = useUpdateEquitySecurity();
  const deleteEquity = useDeleteEquitySecurity();
  const refreshPrice = useRefreshStockPrice();

  // Handler functions
  const handleBack = () => {
    navigate('/investments/securities/equity');
  };

  const handleEdit = () => {
    setEditFormOpen(true);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleSaveEquity = async (data: any) => {
    try {
      await updateEquity.mutateAsync({
        id: id!,
        ...data,
      });
      toast.success('Equity security updated successfully');
      setEditFormOpen(false);
    } catch (error) {
      console.error('Error updating equity:', error);
      toast.error('Failed to update equity security');
    }
  };

  const handleCancelEdit = () => {
    setEditFormOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteEquity.mutateAsync(id!);
      toast.success('Equity security deleted successfully');
      navigate('/investments/securities/equity');
    } catch (error) {
      console.error('Error deleting equity:', error);
      toast.error('Failed to delete equity security');
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  const formatCurrency = (amount: number, currency: string) => {
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currency} ${formatted}`;
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="space-y-4">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show error state
  if (error || !equityDetail) {
    return (
      <ErrorBoundary onReset={() => { window.location.reload(); }}>
        <div className="space-y-6">
          <SecuritiesErrorState
            message="Failed to load equity security details. Please try again."
            onRetry={() => refetch()}
            action={
              <Button onClick={handleBack} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                Back to Equity Securities
              </Button>
            }
          />
        </div>
      </ErrorBoundary>
    );
  }

  const equity = equityDetail as EquitySecurity & { transactions: any[] };
  const profitLoss = calculateEquityProfitLoss(
    equity.shares_owned,
    equity.purchase_price,
    equity.current_price
  );
  const isProfit = profitLoss.profitLoss >= 0;

  // Compute total dividends
  const totalDividends = dividendsData
    ? computeTotalEquityDividends(dividendsData.dividends)
    : 0;

  // Generate month-over-month data (mock data for now - would need historical data)
  const monthlyData = [
    { month: 'Jan', value: profitLoss.currentValue * 0.85 },
    { month: 'Feb', value: profitLoss.currentValue * 0.90 },
    { month: 'Mar', value: profitLoss.currentValue * 0.88 },
    { month: 'Apr', value: profitLoss.currentValue * 0.95 },
    { month: 'May', value: profitLoss.currentValue * 0.98 },
    { month: 'Jun', value: profitLoss.currentValue },
  ];

  return (
    <ErrorBoundary
      onReset={() => {
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="icon"
              aria-label="Back to equity securities"
              className="min-h-[44px] min-w-[44px]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {equity.symbol} - {equity.company_name}
              </h1>
              <p className="text-sm text-muted-foreground sm:text-base">
                {equity.exchange}
              </p>
            </div>
          </div>
          <div className="flex gap-2" role="group" aria-label="Equity actions">
            <Button
              onClick={() => refreshPrice.mutate({ symbol: equity.symbol, exchange: equity.exchange, equityId: id! })}
              variant="outline"
              aria-label="Refresh stock price"
              className="min-h-[44px]"
              disabled={refreshPrice.isPending}
            >
              {refreshPrice.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Refresh Price
            </Button>
            <Button
              onClick={handleEdit}
              variant="outline"
              aria-label="Edit equity security"
              className="min-h-[44px]"
            >
              <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
            <Button
              onClick={handleDelete}
              variant="outline"
              aria-label="Delete equity security"
              className="min-h-[44px]"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </div>
        </div>

        {/* Real-time Price */}
        <Card>
          <CardContent className="p-6">
            <StockPriceDisplay symbol={equity.symbol} exchange={equity.exchange} />
          </CardContent>
        </Card>

        {/* Profit/Loss Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Current Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(profitLoss.currentValue, equity.currency)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Cost Basis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(profitLoss.costBasis, equity.currency)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Profit/Loss
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold flex items-center gap-2 ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                {isProfit ? '+' : ''}{formatCurrency(profitLoss.profitLoss, equity.currency)}
                {isProfit ? (
                  <TrendingUp className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <TrendingDown className="h-5 w-5" aria-hidden="true" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Return %
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                {isProfit ? '+' : ''}{profitLoss.profitLossPercentage.toFixed(2)}%
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Dividends
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isDividendsLoading ? (
                <Skeleton className="h-8 w-32" aria-label="Loading total dividends" />
              ) : (
                <div className="text-2xl font-bold">
                  {formatCurrency(totalDividends, equity.currency)}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Dividend History */}
        <Card>
          <CardHeader>
            <CardTitle>Dividend History</CardTitle>
          </CardHeader>
          <CardContent>
            <DividendHistoryTable
              dividends={dividendsData?.dividends ?? []}
              isLoading={isDividendsLoading}
              isError={isDividendsError}
              totalCount={dividendsData?.totalCount ?? 0}
              currentPage={dividendsPage}
              pageSize={PAGE_SIZE}
              onPageChange={setDividendsPage}
              onRetry={refetchDividends}
            />
          </CardContent>
        </Card>

        {/* Share Purchase Trend */}
        <SharePurchaseTrendChart
          transactions={transactions ?? []}
          isLoading={isTransactionsLoading}
        />

        {/* Holding Information */}
        <Card>
          <CardHeader>
            <CardTitle>Holding Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Shares Owned</p>
                <p className="text-lg font-medium">
                  {equity.shares_owned.toLocaleString(undefined, { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 6 
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Purchase Price</p>
                <p className="text-lg font-medium">
                  {formatCurrency(equity.purchase_price, equity.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Current Price</p>
                <p className="text-lg font-medium">
                  {formatCurrency(equity.current_price, equity.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Purchase Date</p>
                <p className="text-lg font-medium">
                  {formatDate(equity.purchase_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Currency</p>
                <p className="text-lg font-medium">{equity.currency}</p>
              </div>
              {equity.notes && (
                <div className="md:col-span-2 lg:col-span-3">
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-lg font-medium">{equity.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Month-over-Month Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Value Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value, equity.currency)}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke={isProfit ? '#10b981' : '#ef4444'} 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card>
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent>
            {equity.transactions && equity.transactions.length > 0 ? (
              <div className="space-y-3" role="list" aria-label="Transaction list">
                {equity.transactions.map((transaction: any) => {
                  const isDeposit = transaction.transaction_type === 'deposit';
                  const Icon = isDeposit ? ArrowDownCircle : ArrowUpCircle;
                  const iconColor = isDeposit ? 'text-green-600' : 'text-blue-600';

                  return (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                      role="article"
                      onClick={() => navigate('/transactions/securities/' + transaction.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`h-8 w-8 ${iconColor}`} aria-hidden="true" />
                        <div>
                          <p className="font-medium capitalize">{transaction.transaction_type}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(transaction.transaction_date)}
                          </p>
                          {transaction.notes && (
                            <p className="text-sm text-muted-foreground">{transaction.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No transactions recorded yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editFormOpen} onOpenChange={setEditFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="edit-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>Edit Equity Security</DialogTitle>
              <DialogDescription id="edit-dialog-description">
                Update the details of your stock holding.
              </DialogDescription>
            </DialogHeader>
            <EquityForm
              holding={equity}
              onSave={handleSaveEquity}
              onCancel={handleCancelEdit}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. This will permanently delete the equity security
                and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={handleCancelDelete} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel deletion"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of equity security"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
